import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ConfirmationDialog } from "@/components/confirmation-dialog";

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `destructive` styles the confirm button for delete/remove/discard. */
  tone?: "default" | "destructive";
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * App-wide imperative confirmation (replaces `window.confirm`). Mounted once in `main.tsx`.
 * Only one confirmation is shown at a time; a new request cancels (resolves `false`) the previous.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Kept after close so the dialog does not go blank during its exit animation.
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (nextOptions) =>
      new Promise<boolean>((resolve) => {
        settle(false);
        resolverRef.current = resolve;
        setOptions(nextOptions);
        setOpen(true);
      }),
    [settle]
  );

  useEffect(() => () => settle(false), [settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options ? (
        <ConfirmationDialog
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              settle(false);
              setOpen(false);
            }
          }}
          title={options.title}
          description={options.description ?? null}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          confirmVariant={options.tone === "destructive" ? "destructive" : "default"}
          onConfirm={() => settle(true)}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

/**
 * `const confirm = useConfirm(); if (await confirm({ title, description, tone: "destructive" })) …`
 * Resolves `true` on confirm and `false` on cancel, Escape, or when superseded.
 */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return confirm;
}
